import get from "lodash.get"

import { TableDataParams, TableDataResult, TableDataTotals } from "./types"

/**
 * This is a table data processing function.
 * it accepts an array of rows (same shape) and
 * can perform:
 *
 * - static-filtering (data that WONT change in dynamic columns)
 * - sorting
 * - dynamic-filter (data that MAY change in dynamic columns)
 * - pagination
 *
 * All of the features will return an object that can be passed
 * to a table renderer and will include
 * - pagination
 * - data
 * - filterValues
 */

export const TableProcessor = ({
  rows,
  filters,
  modifiers,
  sort,
  sticky, // todo rename this
}: TableDataParams): TableDataResult => {
  const stickyRows =
    Array.isArray(sticky) && sticky.length > 0
      ? sticky
          .map((stickyItem) => {
            // switch on operator
            switch (stickyItem.operator) {
              case "eq": {
                return rows.find((x: Record<string, unknown>) => get(x, stickyItem.field) === stickyItem.unique)
              }
              case "gt": {
                return rows.find((x: any) => get(x, stickyItem.field) > Number(stickyItem?.value ?? 0))
              }
            }

            return false
          })
          .filter(Boolean)
      : []

  // Apply static filters to reduce the dataset
  const staticFilters = filters?.static ?? []
  const combinedFilterFunction = (row: Record<string, unknown>) => {
    // if any of the static filters fail then this row is removed
    return staticFilters.every((filter) => {
      // if this row is a sticky row then always return true!
      if (stickyRows.includes(row)) {
        return true
      }
      // does the user want the whole row?
      if (filter.field === "*") {
        return filter.filterFunction(row)
      }
      // call the static filter function to determine if this row should be removed
      return filter.filterFunction(get(row, filter.field))
    })
  }
  const filteredRows = rows.filter(combinedFilterFunction)

  // ok now we can modify rows
  const preTotalModifierFunctions = modifiers?.preTotals ?? []
  preTotalModifierFunctions.forEach((modifier) => {
    filteredRows.forEach(modifier.modifier)
  })

  // ok, now we can run the totals calcs
  const totals: TableDataTotals = {}
  const totalModifierFunctions = modifiers?.totals ?? []

  totalModifierFunctions.forEach((total) => {
    if (!total.name) {
      return
    }
    totals[total.name] = total.modifier(filteredRows, totals)
  })

  // ok now we can run the post totals modifiers, for things like
  // - what % of the total is this row?
  const postTotalModifierFunctions = modifiers?.postTotals ?? []
  postTotalModifierFunctions.forEach((modifier) => {
    filteredRows.map((row) => modifier.modifier(row, totals))
  })

  // ok now we can filter dynamic values
  const dynamicFilters = filters?.dynamic ?? []
  const combinedDynamicFilterFunction = (row: any) => {
    return dynamicFilters.every((filter) => {
      if (stickyRows.includes(row)) {
        return true
      }

      if (filter.field === "*") {
        return filter.filterFunction(row)
      }
      return filter.filterFunction(get(row, filter.field))
    })
  }
  let postFilteredRows = filteredRows.filter(combinedDynamicFilterFunction)

  if (sort) {
    postFilteredRows = postFilteredRows.sort((a: any, b: any): number => {
      const valueA = get(a, sort.key)
      const valueB = get(b, sort.key)
      switch (sort.type) {
        case "string": {
          if (sort.direction === "asc") {
            return valueA.localeCompare(valueB)
          } else {
            return valueB.localeCompare(valueA)
          }
        }
        case "number": {
          if (isNaN(valueA) || isNaN(valueB)) {
            // console.log("skipping", valueA, valueB)
            return 0
          }
          if (sort.direction === "asc") {
            return valueA - valueB
          } else {
            return valueB - valueA
          }
        }
      }
    })
  }

  // now move sticky rows into their desired positions
  if (Array.isArray(sticky) && sticky.length > 0) {
    sticky.forEach((stickyItem) => {
      // switch on operator
      switch (stickyItem.operator) {
        case "eq": {
          const found = postFilteredRows.find((x) => get(x, stickyItem.field) === stickyItem.unique)
          if (found) {
            postFilteredRows = postFilteredRows.filter((x) => x !== found)
            postFilteredRows.splice(stickyItem.position, 0, found)
          }
          break
        }
        case "gt": {
          const matchingRows = postFilteredRows.filter((x) => get(x, stickyItem.field) > (stickyItem.value ?? 0))

          // Sort matching rows by the specified field
          matchingRows.sort((a, b) => {
            const valueA = get(a, stickyItem.field)
            const valueB = get(b, stickyItem.field)

            // Assuming we want to sort in descending order for 'gt' operator
            return valueB - valueA
          })

          if (matchingRows.length > 0) {
            // Remove matching rows from their current positions
            postFilteredRows = postFilteredRows.filter((x) => !matchingRows.includes(x))

            // Insert matching rows at the beginning of the array
            postFilteredRows.unshift(...matchingRows)
          }

          // console.log("Matching rows moved to position 0:", matchingRows.length)
          break
        }
      }
    })
  }
  return {
    rows: postFilteredRows,
    filters: [],
    pagination: {},
    totals,
  }
}
